import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { Stage as StageType } from '../../types';
import { FetchParams } from './employeesSlice';
import { buildQueryString } from '../../utils/queryStringBuilder';

interface StagesState {
    stages: StageType[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
}

const initialState: StagesState = {
    stages: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 0,
        pageSize: 10,
        total: 0
    }
};

export const fetchStages = createAsyncThunk(
    'stages/fetchStages',
    async (params: FetchParams = {}, { rejectWithValue }) => {
        try {
            // Build query string using the utility function
            const queryString = buildQueryString(params);
            
            const response = await api.get(`/api/stages${queryString}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch stages');
        }
    }
);

export const createStage = createAsyncThunk(
    'stages/create',
    async (stageData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/stages', { data: stageData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to create stage');
        }
    }
);

export const updateStage = createAsyncThunk(
    'stages/update',
    async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/api/stages/${id}`, { data });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to update stage');
        }
    }
);

export const deleteStage = createAsyncThunk(
    'stages/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            await api.delete(`/api/stages/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete stage');
        }
    }
);

const stagesSlice = createSlice({
    name: 'stages',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch stages
            .addCase(fetchStages.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchStages.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.stages = action.payload.data;
                if (action.payload.meta && action.payload.meta.pagination) {
                    state.pagination = {
                        page: action.payload.meta.pagination.page - 1, // Convert to 0-based for frontend
                        pageSize: action.payload.meta.pagination.pageSize,
                        total: action.payload.meta.pagination.total
                    };
                }
            })
            .addCase(fetchStages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create stage
            .addCase(createStage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createStage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stages.push(action.payload.data);
            })
            .addCase(createStage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Update stage
            .addCase(updateStage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateStage.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedStage = action.payload.data;
                const index = state.stages.findIndex(stage => stage.id === updatedStage.id);
                if (index !== -1) {
                    state.stages[index] = updatedStage;
                }
            })
            .addCase(updateStage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Delete stage
            .addCase(deleteStage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteStage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.stages = state.stages.filter(stage => stage.id !== action.payload);
            })
            .addCase(deleteStage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    }
});

export default stagesSlice.reducer; 